<script setup lang="ts">
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmSelectBadge,
  TirPmIconView,
  TirCheckbox,
} from "tir-components";
import { computed, inject, PropType, ref, watch } from "vue";
import { EmployeeAccessModel, EmployeeModel } from "../../models";
import { EmployeeQueueAccessEnum } from "../../enums";
import { AddEmployeeDropdown } from "./";
import TirPmSearchInput from "tir-pm-search-input/src/TirPmSearchInput.vue";
import { TirPmTable, TirPmTableTh, TirPmTableTr, TirPmTableTd } from "tir-pm-table";
import {
  Bars3BottomLeftIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  InformationCircleIcon,
  PlusIcon,
  ScissorsIcon,
} from "tir-style-system/icons/outline";
import { UserItem } from "../";

import TirPmSystemMessage from "tir-pm-system-message/src/tir-pm-system-message.vue";
import { TaskParametersLeave } from "@/components/task-parameters-form/components";
import { NeutralInformation } from "tir-style-system/icons/solid";
import { TirPmTooltipElement, TirPmTooltipElementPlacementEnum } from "tir-pm-tooltip-element";
import { AccessAdapterService } from "@/components/task-forms/components/task-detail/adapter/AccessAdapterService";
import { useRoute, useRouter } from "vue-router";
import { QueueTasksModel } from "@/components/sidebar/models";
import { CurrentUser } from "tir-pm-user-core";
/**Параметры */
const props = defineProps({
  /**Заголовок */
  title: { type: String },
  /**Список добавленных к очереди пользователей */
  queueUsers: { type: Array as PropType<EmployeeAccessModel[]> },
  queueDeletedUsers: { type: Array as PropType<EmployeeModel[]> },
  /**Список пользователей */
  allUsers: { type: Array as PropType<EmployeeModel[]> },
  /**Главный список (отсутствуют кнопки _buttons) */
  access: { type: Number as PropType<EmployeeQueueAccessEnum> },
  /**Можно ли добавлять всех пользователей */
  allowAddEveryone: { type: Boolean },

  /**Id редактируемой очереди */
  queue: { type: Object as PropType<QueueTasksModel> },
});
/**События */
const emit = defineEmits<{
  /**Добавить всех пользователей */
  (e: "addEveryone", access: EmployeeQueueAccessEnum): void;
  /**Удалить всех пользователей */
  (e: "removeEveryone", access: EmployeeQueueAccessEnum): void;
  /**Выбор пользователя */
  (e: "selectUser", userId: number, access: EmployeeQueueAccessEnum): void;
  (e: "onChange", value: boolean): void;
  (e: "onSave"): void;
  (e: "onClose"): void;
  /**Удаление пользователя */
  (e: "removeUser", userId: number, access: EmployeeQueueAccessEnum): void;
}>();
const columnTableSetting = ref([
  "minmax(12.5rem, 1fr)",
  // 'minmax(5.563rem, 5.563rem)',
  "minmax(6.25rem, 6.25rem)",
  "minmax(7.75rem, 7.75rem)",
  "minmax(7.75rem, 7.75rem)",
  "minmax(7.7rem, 7.7rem)",
]);

/**Главный список пользователей */
const main = computed(() => props.access == undefined);

/**Список id уже добавленных пользователей */
const addedUsersIds = computed(() => props.queueUsers.map((u) => u.Id));
/**Отображаемый список пользователей */
const options = computed(
  () => props.allUsers?.filter((u) => !addedUsersIds.value.includes(u.Id)) || [],
);
/**Окно для подтверждения закрытия настроек параметров */
const $taskParametersLeave = ref<InstanceType<typeof TaskParametersLeave>>();
/**Добавить всех */
const addEveryone = () => emit("addEveryone", props.access);
/**Удалить всех */
const removeEveryone = () => emit("removeEveryone", props.access);
/**Выбор пользователя */
const selectUser = (userId: number) => emit("selectUser", userId, props.access);
/**Удаление пользователя */
const removeUser = (userId: number) => emit("removeUser", userId, props.access);
const isChanged = ref(false);
const colCheckBox = ref([false, false, false, false, false]);
const colUnselect = ref([false, false, false, false, false]);
const userNameSearch = ref("");

/**сохранить данные */
function changeData() {
  isChanged.value = false;
  emit("onSave");
}

function checkCheckboxesStateCol(col: number) {
  let sum = 0;
  props.queueUsers.forEach((user) => {
    sum = user.Accesses[col] ? sum + 1 : sum;
  });
  if (sum === 0) {
    colUnselect.value[col] = false;
    colCheckBox.value[col] = false;
  } else if (sum === props.queueUsers.length) {
    colUnselect.value[col] = false;
    colCheckBox.value[col] = true;
  } else {
    colUnselect.value[col] = true;
    colCheckBox.value[col] = true;
  }
}

watch(
  () => props.queueUsers?.length,
  () => {
    for (let index = 0; index < 4; index++) {
      checkCheckboxesStateCol(index);
    }
  },
);

function onChangeAccessQueue(value: EmployeeAccessModel, col: number) {
  isChanged.value = true;
  emit("onChange", true);

  // Чекбокс включен у сотрудника
  if (value.Accesses[col]) {
    switch (col) {
      case 1:
      case 3:
        value.Accesses[2] = true;
        break;
    }
  } else {
    switch (col) {
      case 2:
        value.Accesses[1] = false;
        value.Accesses[3] = false;
        break;
    }
  }

  for (let index = 0; index < 4; index++) {
    checkCheckboxesStateCol(index);
  }
}

function setStartPositions() {
  for (let i = 0; i < 4; i++) {
    let sum = 0;
    props.queueUsers.forEach((user) => {
      sum = user.Accesses[i] ? sum + 1 : sum;
    });
    colCheckBox.value[i] = sum == 4;
    colUnselect.value[i] = sum == 4;
  }
}

/**Открыть окно с подтверждением выхода из настройки */
const openLeaveConfirmation = (callback?: Function) => {
  $taskParametersLeave.value?.toggle(true, callback);
};

function update(user: EmployeeAccessModel) {
  isChanged.value == true;
  emit("onChange", true);
}
const router = useRouter();
const adapter = new AccessAdapterService();
function addPerformer() {
  emit("onClose");
  router.push({
    name: "namespace-team",
    params: {
      projectId: route.params.projectId,
      namespaceId: props.queue.NamespaceId,
      queueId: props.queue.Id,
    },
  });
}
function changeColumnData(value: number) {
  props.queueUsers.forEach((user) => {
    user.Accesses[value] = colCheckBox.value[value];
    if (colCheckBox.value[value]) {
      switch (value) {
        case 1:
        case 3:
          user.Accesses[2] = true;
          break;
      }
    } else {
      switch (value) {
        case 2:
          user.Accesses[1] = false;
          user.Accesses[3] = false;
          break;
      }
    }
  });
  for (let index = 0; index < 4; index++) {
    checkCheckboxesStateCol(index);
  }
  isChanged.value = true;
  emit("onChange", true);
}
const route = useRoute();
async function isCanAddPerformer() {
  let resp = true;
  let u = CurrentUser;
  await adapter.isNamespaceAdmin({ NamespaceId: props.queue.NamespaceId, UserId: u.Id });
  await adapter.isProjectAdmin({ ProjectId: Number(route.params.projectId), UserId: u.Id });
  resp = adapter.IsProjectAdmin || adapter.IsNamespaceAdmin || u.Roles.includes(2);
  return resp;
}
</script>
<template>
  <div class="edit-queue-modal-choose-users">
    <div class="edit-queue-modal-choose-users__wrap-title">
      <div class="edit-queue-modal-choose-users_title" :class="{ main }" v-text="title" />
      <TirPmSearchInput
        class="ml-auto"
        :is-small="true"
        v-model="userNameSearch"
        label="Имя пользователя"
        placeholder="Имя пользователя"
      ></TirPmSearchInput>

      <TirPmButton
        @click="changeData"
        class="tasks-kanban__header__add ml-2"
        :variant="
          !isChanged ? TirPmButtonVariantEnum.SecondaryState : TirPmButtonVariantEnum.PrimaryState
        "
        :size="TirPmButtonSizeEnum.Small"
        :is-disabled="!isChanged"
      >
        Применить изменения
      </TirPmButton>
    </div>
    <!-- <div class="edit-queue-modal_border" /> -->
    <!-- <div v-for="(user, idx) in allUsers" :key="idx">
      <UserItem :user="user"></UserItem>
    </div> -->
    <div style="width: 52.2rem; overflow: hidden">
      <TirPmTable :columns="columnTableSetting" :isBordered="false" style="overflow: auto">
        <template #emptyDataSlot>
          <TirPmSystemMessage
            style="width: 100%; margin: 5rem 0rem"
            v-if="!(queueUsers?.length > 0)"
          >
            <template #topDescImage> </template>
            <template #mainTitleSlot>Добавьте сотрудников в команду рабочей области</template>
            <template #actionsSlot
              ><TirPmButton
                v-if="isCanAddPerformer()"
                :variant="TirPmButtonVariantEnum.SecondaryState"
                :size="TirPmButtonSizeEnum.Middle"
                @click="addPerformer"
              >
                Добавить
              </TirPmButton>
            </template>
          </TirPmSystemMessage>
        </template>
        <template #tableHead>
          <TirPmTableTh :isLeftBorder="false">
            <div class="wrap-title" style="justify-content: start; align-items: center">
              <div>ФИО сотрудника</div>
              <!-- <BarsArrowDownIcon style="width:1rem;margin-left:1rem;"/>
            <BarsArrowUpIcon style="width:1rem;margin-left:1rem;"/> -->
              <Bars3BottomLeftIcon style="width: 1rem; margin-left: 1rem" />
            </div>
          </TirPmTableTh>

          <TirPmTableTh :isLeftBorder="true">
            <div class="wrap-title">
              <div>
                Добавление<br />
                задач
              </div>
              <TirCheckbox
                :isUnselect="colUnselect[0]"
                v-model="colCheckBox[0]"
                @update:model-value="changeColumnData(0)"
              >
              </TirCheckbox>
            </div>
          </TirPmTableTh>
          <TirPmTableTh :isLeftBorder="true">
            <div class="wrap-title">
              <div>Редактирование <br />чужих задач</div>
              <TirCheckbox
                :isUnselect="colUnselect[1]"
                v-model="colCheckBox[1]"
                @update:model-value="changeColumnData(1)"
              >
              </TirCheckbox>
            </div>
          </TirPmTableTh>
          <TirPmTableTh :isLeftBorder="true">
            <div class="wrap-title">
              <div>
                Просмотр чужих<br />
                задач
              </div>
              <TirCheckbox
                :isUnselect="colUnselect[2]"
                v-model="colCheckBox[2]"
                @update:model-value="changeColumnData(2)"
              >
              </TirCheckbox>
            </div>
          </TirPmTableTh>
          <TirPmTableTh :isLeftBorder="true">
            <div class="wrap-title">
              <div>Итоговая оценка<br />времени</div>
              <TirCheckbox
                :isUnselect="colUnselect[3]"
                v-model="colCheckBox[3]"
                @update:model-value="changeColumnData(3)"
              >
              </TirCheckbox>
            </div>
          </TirPmTableTh>
        </template>
        <template #tableBody>
          <TirPmTableTr :columns="columnTableSetting" v-for="(user, idx) in queueUsers" :key="idx">
            <TirPmTableTd style="overflow: visible !important">
              <UserItem :user="user"></UserItem>
            </TirPmTableTd>
            <TirPmTableTd style="overflow: visible !important">
              <TirCheckbox
                class="ml-auto"
                v-model="user.Accesses[0]"
                @update:model-value="onChangeAccessQueue(user, 0)"
              />
            </TirPmTableTd>
            <TirPmTableTd style="overflow: visible !important">
              <TirCheckbox
                class="ml-auto"
                v-model="user.Accesses[1]"
                @update:model-value="onChangeAccessQueue(user, 1)"
              />
            </TirPmTableTd>
            <TirPmTableTd style="overflow: visible !important">
              <TirCheckbox
                class="ml-auto"
                v-model="user.Accesses[2]"
                @update:model-value="onChangeAccessQueue(user, 2)"
              />
            </TirPmTableTd>
            <TirPmTableTd style="overflow: visible !important">
              <TirCheckbox
                class="ml-auto"
                v-model="user.Accesses[3]"
                @update:model-value="onChangeAccessQueue(user, 3)"
              />
            </TirPmTableTd>
          </TirPmTableTr>
        </template>
      </TirPmTable>
    </div>
    <div class="edit-queue-modal_border" />
    <div class="deleted-users">
      <div class="delete-info">
        <div style="font-weight: bold">Авторы тикетов, удаленные из очереди</div>
        <TirPmTooltipElement
          class="tooltip-element"
          :position="TirPmTooltipElementPlacementEnum.Top"
        >
          <NeutralInformation />
          <template #content>
            <p style="width: 18rem">
              Список только тех удаленных Авторов чьи тикеты еще есть в очереди
            </p>
          </template>
        </TirPmTooltipElement>
      </div>
      <UserItem
        class="deleted-user"
        :user="delUser"
        v-for="(delUser, idx) of queueDeletedUsers"
        :queue="queue"
        :key="idx"
      ></UserItem>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.tooltip-element {
  width: 1rem;
  margin-left: 1rem;
}
.table-container {
  padding: unset !important;
}

.edit-queue-modal-wrapper_slot {
  padding: 0.6rem !important;
}

.ml-auto {
  margin-left: auto;
}

.ml-2 {
  margin-left: 1rem;
}

.edit-queue-modal-choose-users {
  display: flex;
  flex-direction: column;
  min-width: 17.3rem;

  .deleted-users {
    display: flex;

    flex-direction: column;
    padding: 1.1rem;

    .deleted-user {
      padding: 0.5rem 0rem;
      color: var(--status-inverted-negative);
    }

    .delete-info {
      display: flex;
      align-items: center;
    }
  }

  .wrap-title {
    display: flex;
    justify-content: space-between;
    width: 100%;
    color: var(--text-tertiary);
    font-size: 0.688rem;
    font-weight: normal;
  }

  &__wrap-title {
    display: flex;
    align-items: center;
    margin-bottom: 2.25rem;
  }

  &_title {
    margin-bottom: 1rem;
    color: var(--text-secondary);

    &.main {
      margin-bottom: 0.5rem;
      color: var(--text-primary);
    }
  }

  &_list {
    display: inline-flex;
    max-width: 100%;
    flex-wrap: wrap;
    gap: 0.25rem 0.125rem;
  }

  &_buttons {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;

    &_add-everyone.button--primary {
      background-color: var(--neutral-translucent-10);
    }

    &_clear.button--primary {
      background-color: #fff;
      transition: 0.1s ease;

      &:not(.button--disabled) {
        color: var(--text-primary);

        &:hover {
          background-color: var(--neutral-translucent-5);
        }
      }
    }
  }
}
</style>
